import { BackupCodecPolicy, supportsAV1, supportsH265, supportsVP9, type TrackPublishOptions, type VideoCodec } from 'livekit-client'
import { shareBitrate, shareCapture, type ScreenShareQuality } from './screenShare'

export interface EncodingTarget {
  width: number
  height: number
  framerate: number
  bitrate: number
  scalabilityMode: 'L1T3' | 'L3T3_KEY'
}

type EncodingInfo = Pick<MediaCapabilitiesInfo, 'supported' | 'smooth' | 'powerEfficient'>
export interface CodecEnvironment {
  codecs: () => VideoCodec[]
  encodingInfo?: (config: MediaEncodingConfiguration) => Promise<EncodingInfo>
}

export const VIDEO_CODEC_CHOICES: { value: VideoCodec; label: string }[] = [
  { value: 'h264', label: 'H.264' },
  { value: 'h265', label: 'H.265' },
  { value: 'vp9', label: 'VP9' },
  { value: 'vp8', label: 'VP8' },
  { value: 'av1', label: 'AV1' },
]
const ORDER: VideoCodec[] = ['av1', 'h265', 'vp9', 'h264', 'vp8']
const MIME: Record<VideoCodec, string> = { av1: 'video/AV1', h265: 'video/H265', vp9: 'video/VP9', h264: 'video/H264', vp8: 'video/VP8' }
const advanced = (codec: VideoCodec) => codec === 'av1' || codec === 'h265' || codec === 'vp9'

/** powerEfficient — оценка браузера, а не доказательство использования GPU. */
export function chooseVideoCodec(codecs: VideoCodec[], info: Partial<Record<VideoCodec, EncodingInfo>>): VideoCodec {
  const efficient = ORDER.find((codec) => codecs.includes(codec) && info[codec]?.supported && info[codec]?.smooth && info[codec]?.powerEfficient)
  if (efficient) return efficient
  // Без оценки эффективности не включаем тяжёлый современный кодек лишь по его наличию.
  if (codecs.includes('h264') && info.h264?.supported !== false && info.h264?.smooth !== false) return 'h264'
  return 'vp8'
}

function browserCodecs(): VideoCodec[] {
  try {
    const caps = RTCRtpSender.getCapabilities('video')
    const available = ORDER.filter((codec) => caps?.codecs.some((c) => c.mimeType.toLowerCase() === MIME[codec].toLowerCase()))
    return available.filter((codec) => codec === 'av1' ? supportsAV1() : codec === 'vp9' ? supportsVP9() : codec === 'h265' ? supportsH265() : true)
  } catch {
    return ['vp8']
  }
}

/** Ограничиваем ожидание: неподдерживаемый API не должен задерживать запуск стрима. */
async function probe(env: CodecEnvironment, codec: VideoCodec, target: EncodingTarget): Promise<EncodingInfo | undefined> {
  if (!env.encodingInfo) return undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const { scalabilityMode, ...video } = target
    const config: MediaEncodingConfiguration = {
      type: 'webrtc',
      video: { ...video, contentType: MIME[codec], ...((codec === 'av1' || codec === 'vp9') ? { scalabilityMode } : {}) },
    }
    return await Promise.race([
      env.encodingInfo(config),
      new Promise<undefined>((resolve) => { timer = setTimeout(() => resolve(undefined), 600) }),
    ])
  } catch {
    return undefined
  } finally {
    clearTimeout(timer)
  }
}

/** Кэш готовим при входе в голос и выборе качества, без await перед getDisplayMedia. */
export class AutoVideoCodec {
  private results = new Map<string, { codec: VideoCodec; backup: VideoCodec }>()
  private pending = new Map<string, Promise<void>>()
  constructor(private env: CodecEnvironment) {}

  private key(target: EncodingTarget) { return JSON.stringify(target) }

  prepare(target: EncodingTarget): Promise<void> {
    const key = this.key(target)
    if (this.results.has(key)) return Promise.resolve()
    const pending = this.pending.get(key)
    if (pending) return pending
    const codecs = this.env.codecs()
    const job = Promise.all(codecs.map(async (codec) => [codec, await probe(this.env, codec, target)] as const)).then((entries) => {
      const info = Object.fromEntries(entries.filter((entry) => entry[1] !== undefined))
      this.results.set(key, { codec: chooseVideoCodec(codecs, info), backup: chooseVideoCodec(codecs.filter((codec) => !advanced(codec)), info) })
      this.pending.delete(key)
    })
    this.pending.set(key, job)
    return job
  }

  supports(codec: VideoCodec): boolean { return this.env.codecs().includes(codec) }

  options(target: EncodingTarget, preferred?: VideoCodec): TrackPublishOptions {
    const codecs = this.env.codecs()
    // An explicit choice applies to this publication, without changing the automatic cache.
    if (preferred) {
      const videoCodec = preferred
      if (!codecs.includes(videoCodec)) {
        const label = VIDEO_CODEC_CHOICES.find((c) => c.value === videoCodec)?.label ?? videoCodec
        throw new Error(`${label} недоступен в этом клиенте`)
      }
      return {
        videoCodec, backupCodec: false,
        ...((videoCodec === 'av1' || videoCodec === 'vp9') ? { scalabilityMode: target.scalabilityMode } : {}),
      }
    }
    const selected = this.results.get(this.key(target))
    const videoCodec = selected && codecs.includes(selected.codec) ? selected.codec : chooseVideoCodec(codecs, {})
    const backup = selected?.backup === 'h264' && codecs.includes('h264') ? 'h264' : 'vp8'
    void this.prepare(target)
    return {
      videoCodec,
      backupCodec: advanced(videoCodec) ? { codec: backup } : false,
      // Просим SFU перевести зрителей на совместимый кодек, не кодировать два потока постоянно.
      // PREFER_REGRESSION — предпочтение; сервер может кратковременно задействовать оба кодека.
      backupCodecPolicy: BackupCodecPolicy.PREFER_REGRESSION,
      ...((videoCodec === 'av1' || videoCodec === 'vp9') ? { scalabilityMode: target.scalabilityMode } : {}),
    }
  }
}

export const autoVideoCodec = new AutoVideoCodec({
  codecs: browserCodecs,
  encodingInfo: (config) => navigator.mediaCapabilities.encodingInfo(config),
})
export const CAMERA_ENCODING: EncodingTarget = { width: 1280, height: 720, framerate: 30, bitrate: 1_700_000, scalabilityMode: 'L3T3_KEY' }
export function screenEncoding(q: ScreenShareQuality): EncodingTarget {
  const { frameRate, ...size } = shareCapture(q)
  return { ...size, framerate: frameRate, bitrate: shareBitrate(q), scalabilityMode: 'L1T3' }
}
